// The Ägarbyte stage of a signed, paid deal — the stage after the payment, on its own page.
//
// ONE STAGE, TWO SIDES. The seller acts: the transfer is reported in Transportstyrelsen's own
// e-service, and the seller then states here that they have done it. The buyer waits, on a screen
// that names who is acting and says there is nothing for the buyer to do. The seller's statement
// is what resolves the stage for both of them.
//
// TRUTH. Klargo holds no answer from Transportstyrelsen — no integration, no callback. The stage
// says what the seller has stated, attributed to the seller, and what Transportstyrelsen does
// next. It never says the buyer is the registered owner. The statement itself is one append-only
// row in the deal's log, which is what ContractOwnership/GetState reads.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ownershipApi } from "./ownershipApi";
import { DealIdentityBand, firstName } from "../payment/PaymentParts";
import { formatPersonName } from "../../../lib/formatName";
import { DEAL_HOME_PATH, announceDealCompleted } from "../../../lib/dealSession";
import SellerFlowTrack from "../SellerFlowTrack";
import BuyerOwnershipPanel from "./BuyerOwnershipPanel";
import SellerOwnershipTask from "./SellerOwnershipTask";
import { OWNERSHIP_CONTENT_MAX, OwnershipResolved } from "./OwnershipParts";

/** How often a waiting buyer asks whether the seller has reported it. The payment stage's rate. */
export const OWNERSHIP_POLL_MS = 5000;

// The deal's progress rail, carried on from the payment stage: Ägarbyte is the step after the
// payment, and it is the one in progress whenever this stage is on screen.
export const OWNERSHIP_TRACK = [
  { key: "vehicle", atStep: 0 },
  { key: "registration", atStep: 1 },
  { key: "connection", atStep: 2 },
  { key: "contract", atStep: 3 },
  { key: "payment", atStep: 4 },
  { key: "ownership", atStep: 5 },
];
const OWNERSHIP_TRACK_STEP = 5;

/** How long the buyer reads "the seller has reported the transfer" before Försäkra bilen opens. */
export const OWNERSHIP_TO_INSURANCE_MS = 5000;

/**
 * How long the rail is given to draw Ägarbyte's tick before the closing page takes over — the
 * reference's FLOW_HANDOFF_MS: the ring resolves, then the tick draws, and the swap follows the
 * check, never overlaps it.
 */
export const OWNERSHIP_HANDOFF_MS = 480;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// As on the payment stage: the page holds the router and passes the move in, so this stage can be
// rendered — and its redirects asserted — on its own. The fallback is a real navigation.
const defaultAdvance = (to) => { window.location.assign(to); };

export default function DealOwnershipStage({ contractId, onAdvance = defaultAdvance }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [asserting, setAsserting] = useState(false);
  const [assertFailed, setAssertFailed] = useState(false);

  /** The seller's own statement, made here: it has lost the button, so focus moves to the heading. */
  const actedRef = useRef(false);
  /** The seller's statement arrived WHILE this party waited: it settles in and is announced. */
  const [reportedLive, setReportedLive] = useState(false);
  const lastStageRef = useRef(null);

  // Ägarbyte completes on the rail, then the seller's sale is finished. One path to the closing page,
  // for the statement made here and for a return to a stage found already reported; with motion
  // reduced there is no tick to wait for, so it moves at once.
  const [leaving, setLeaving] = useState(false);
  const leaveTimerRef = useRef(null);
  useEffect(() => () => window.clearTimeout(leaveTimerRef.current), []);
  const leaveForCompletion = useCallback(() => {
    setLeaving(true);
    window.clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = window.setTimeout(
      () => onAdvance(`/Completion/${contractId}`),
      prefersReducedMotion() ? 0 : OWNERSHIP_HANDOFF_MS,
    );
  }, [contractId, onAdvance]);

  const refresh = useCallback(async () => {
    try {
      const next = await ownershipApi.getState(contractId);
      setState((prev) => (prev && next && prev.stage === next.stage ? prev : next));
    } catch (_) {
      setFailed(true);
    }
  }, [contractId]);

  useEffect(() => {
    if (!contractId) return undefined;
    refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [contractId, refresh]);

  // Only a waiting party has anything to wait for. Once the stage is reported there is nothing
  // further this page can learn, so it stops asking.
  useEffect(() => {
    if (!contractId || !state || state.stage !== "pending") return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, OWNERSHIP_POLL_MS);
    return () => window.clearInterval(timer);
  }, [contractId, state, refresh]);

  // The live edge, read off the stages themselves rather than off render timing: only
  // pending → reported observed here is one. A repeated "reported" is no edge.
  useEffect(() => {
    if (lastStageRef.current === "pending" && state?.stage === "reported") setReportedLive(true);
    if (state) lastStageRef.current = state.stage;
    // Reported = the sale is complete: close this party's live connection to the deal.
    if (state?.stage === "reported") announceDealCompleted(contractId);
  }, [state, contractId]);

  // The buyer has nothing further to do here once the seller has reported the transfer: after the
  // reported message has been read for a moment, they are taken to "Försäkra bilen" (a demo page,
  // hard-coded in the front end). The seller's own way on is the closing page, above.
  const buyerReported = state?.stage === "reported" && state?.role !== "seller";
  // onAdvance is an inline arrow on the page: read through a ref so a re-render cannot restart the wait.
  const advanceRef = useRef(onAdvance);
  advanceRef.current = onAdvance;
  useEffect(() => {
    if (!buyerReported) return undefined;
    const timer = window.setTimeout(() => advanceRef.current(`/Insurance/${contractId}`), OWNERSHIP_TO_INSURANCE_MS);
    return () => window.clearTimeout(timer);
  }, [buyerReported, contractId]);

  // Ägarbyte belongs to a deal whose payment the seller has CONFIRMED. Anything earlier belongs on
  // the payment stage, which states where the money actually stands; a deal this user is not a
  // party to, or that cannot be read at all, belongs on the overview.
  useEffect(() => {
    if (failed) { onAdvance(DEAL_HOME_PATH); return; }
    if (!state || state.stage !== "notOpen") return;
    onAdvance(state.notOpenReason === "paymentNotConfirmed" ? `/Payment/${contractId}` : DEAL_HOME_PATH);
  }, [state, failed, contractId, onAdvance]);

  const assert = async () => {
    actedRef.current = true;
    setAsserting(true);
    setAssertFailed(false);
    try {
      setState(await ownershipApi.reportTransfer(contractId));
      // The statement IS the progression: the resolved state stands while Ägarbyte completes on the
      // rail, and the seller moves on by themselves.
      leaveForCompletion();
    } catch (_) {
      setAssertFailed(true);
    } finally {
      setAsserting(false);
    }
  };

  // Until the stage knows where the deal stands — and that this user belongs here — it shows the
  // shape of what is coming and says which stage that is, as the reference does.
  if (!state || state.stage === "notOpen") {
    return (
      <div className="mx-auto w-full" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }} role="status" aria-busy="true"
        data-testid="ownership-preparing">
        <span className="sr-only">{t("ownership.preparing")}</span>
        <div className="border-t-2 border-ds-ink pt-4" aria-hidden="true">
          <div className="h-6 w-28 animate-pulse rounded bg-[rgba(20,36,46,.08)]" />
        </div>
        <div className="mt-8 h-7 w-2/3 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
        <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
      </div>
    );
  }

  const isSeller = state.role === "seller";
  // Display only: legacy accounts may hold BankID-style CAPS names.
  const sellerName = formatPersonName(state.sellerName) || t("payment.sellerFallback");
  const buyerName = formatPersonName(state.buyerName) || t("ownership.buyerFallback");
  const vars = {
    seller: sellerName,
    sellerFirst: firstName(sellerName) || sellerName,
    buyer: buyerName,
    buyerFirst: firstName(buyerName) || buyerName,
  };
  const reported = state.stage === "reported";

  return (
    <>
      {/* The rail sits at page level, exactly as it does on the payment and signing pages. */}
      <div className="flow-shell-track">
        <SellerFlowTrack activeStep={OWNERSHIP_TRACK_STEP} t={t} milestones={OWNERSHIP_TRACK}
          ns={isSeller ? "seller" : "buyer"} completing={leaving} />
      </div>
      <section className="mx-auto w-full pb-8" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }}
        aria-labelledby="deal-ownership-heading" data-testid="deal-ownership-stage">
        <span id="deal-ownership-heading" className="sr-only">{t("ownership.kicker")}</span>
        {/* The same record header the payment stage opens with, from the same signed contract, so
            the stage reads as the same transaction rather than a new thing. */}
        <DealIdentityBand plate={state.registrationNo} amount={state.amount} language={language} t={t} />

        {/* Nobody acted on a page that is merely told, so the statement is announced politely,
            once, from a region that was already here. The party who pressed the button is
            announced by focus instead (OwnershipResolved, announce="focus"). */}
        <span className="sr-only" role="status" aria-live="polite" data-testid="ownership-live-announcement">
          {reportedLive && !actedRef.current
            ? `${t("ownership.reported.title")}. ${t(isSeller ? "ownership.seller.reported.lead" : "ownership.buyer.reported.lead", vars)}`
            : ""}
        </span>

        {reported ? (
          <OwnershipResolved
            lead={t(isSeller ? "ownership.seller.reported.lead" : "ownership.buyer.reported.lead", vars)}
            meta={t(isSeller ? "ownership.seller.reported.meta" : "ownership.buyer.reported.meta")}
            announce={actedRef.current ? "focus" : "none"}
            // Only the seller has a stage after this one, and only a seller who FOUND it reported
            // needs a control: a statement made here moves on by itself.
            onContinue={isSeller && !actedRef.current ? leaveForCompletion : undefined}
            continuing={leaving}
            t={t}
          />
        ) : isSeller ? (
          <SellerOwnershipTask
            buyerName={buyerName}
            plate={state.registrationNo}
            nationalId={state.buyerNationalId}
            onAssert={assert}
            asserting={asserting}
            failed={assertFailed}
            t={t}
          />
        ) : (
          <BuyerOwnershipPanel vars={vars} t={t} />
        )}
      </section>
    </>
  );
}
